import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Microscope, MapPin, Search } from "lucide-react";
import { formatExamDate, formatRiskBadge } from "@/lib/formatters/examFormatter";

interface SmartfileSession {
  id: string;
  file_name: string;
  content_type: string | null;
  ai_summary?: string | null;
  created_at: string;
  structured_records?: any[] | null;
  patient_id: string | null;
  status: string | null;
}

interface SmartfileAnatomopathologyDetailsProps {
  session: SmartfileSession;
  onRefresh: () => void;
  expandAllByDefault?: boolean;
}

export function SmartfileAnatomopathologyDetails({ session }: SmartfileAnatomopathologyDetailsProps) {
  const anatomopathologyRecords = (session.structured_records || []).filter(
    (record: any) => typeof record === 'object' && record !== null && record.type === 'anatomopathology'
  );

  if (anatomopathologyRecords.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground bg-background border-t">
        <p className="text-sm">Nenhum registro de anatomia patológica encontrado nesta sessão</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 bg-background border-t">
      <div className="flex items-center gap-2 mb-2">
        <Microscope className="h-5 w-5 text-rose-600" />
        <h3 className="text-lg font-semibold text-rose-600">Biópsia / Anatomia Patológica</h3>
        <Badge variant="secondary">{anatomopathologyRecords.length} documento(s)</Badge>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {anatomopathologyRecords.map((record: any, index: number) => {
          const riskBadge = formatRiskBadge(record.risk_level || 'baixo');
          const formattedDate = record.event_date
            ? formatExamDate(record.event_date)
            : 'Data não especificada';

          return (
            <Card key={index} className="overflow-hidden border-l-4 border-l-rose-500 shadow-sm">
              <CardContent className="p-4 space-y-4">
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-base leading-tight">
                      {record.exam_type || 'Exame Histopatológico'}
                    </h4>
                    <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                      <span>{formattedDate}</span>
                      {record.specimen_type && (
                        <>
                          <span className="w-1 h-1 rounded-full bg-muted-foreground/30" />
                          <span>{record.specimen_type}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <Badge variant={riskBadge.variant} className="whitespace-nowrap shrink-0">
                    {riskBadge.emoji} {riskBadge.label}
                  </Badge>
                </div>

                {/* Sítios / Material */}
                {record.specimen_sites && record.specimen_sites.length > 0 && (
                  <div className="bg-rose-50/50 dark:bg-rose-950/10 rounded-lg p-3 border border-rose-100 dark:border-rose-900/30">
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin className="h-3.5 w-3.5 text-rose-600" />
                      <span className="text-xs font-semibold text-rose-700 dark:text-rose-300">Material Examinado</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5 pl-5">
                      {record.specimen_sites.map((site: string, idx: number) => (
                        <Badge key={idx} variant="outline" className="text-[10px] bg-background">
                          {site}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Diagnósticos */}
                {record.histopathological_diagnoses && record.histopathological_diagnoses.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Search className="h-3.5 w-3.5 text-rose-600" />
                      <span className="text-xs font-semibold text-rose-700 dark:text-rose-300 uppercase tracking-wider">Conclusão Histopatológica</span>
                    </div>
                    <div className="space-y-2 pl-5">
                      {record.histopathological_diagnoses.map((diag: any, idx: number) => (
                        <div key={idx} className="relative pl-3 before:absolute before:left-0 before:top-1 before:bottom-1 before:w-[2px] before:bg-rose-200 dark:before:bg-rose-800">
                          {diag.site && (
                            <p className="text-[10px] font-bold text-rose-600 dark:text-rose-400 mb-0.5">
                              {diag.site}
                            </p>
                          )}
                          <p className="text-sm font-medium leading-relaxed">
                            {diag.diagnosis}
                          </p>
                          {diag.details && (
                            <p className="text-xs text-muted-foreground mt-1 bg-muted/30 p-2 rounded italic">
                              {diag.details}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Descrição Macroscópica */}
                {record.macroscopic_exam && (
                  <div className="pt-2 border-t border-dashed">
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest mb-1.5">Descrição Macroscópica</p>
                    <p className="text-xs text-muted-foreground line-clamp-3 hover:line-clamp-none transition-all cursor-pointer bg-muted/20 p-2 rounded">
                      {record.macroscopic_exam}
                    </p>
                  </div>
                )}

                {/* Médico Patologista */}
                {(record.doctor_name || record.doctor_crm) && (
                  <div className="flex items-center justify-end gap-1.5 text-[10px] text-muted-foreground mt-2 opacity-70">
                    <span className="font-semibold uppercase truncate">Responsável:</span>
                    <span className="truncate">
                      {record.doctor_name}
                      {record.doctor_crm && ` (CRM ${record.doctor_crm})`}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
